/**
 * A halt's recorded detail, and any error that may copy it, as this caller may see it (D8).
 *
 * The server serves the detail — file names, headroom — only to a caller passing the Lighthouse.Admin policy, and a
 * `…Withheld: true` flag to anyone else. Three states, never two: WITHHELD (something was recorded; render
 * DETAIL_ADMIN_ONLY, dimmed), NONE (nothing was recorded), RECORDED (the text). The server's flag decides — never the
 * caller's role on this side, which the Development carve-out would get wrong. A flag an older API does not send
 * reads as false.
 *
 * Pure: imports nothing, so it runs under vitest's node environment as-is.
 */

/** The separator EtlHaltController (HaltDetailDisclosure.ClearedNoteSeparator) appends a clearing note after. */
export const HALT_CLEARED_MARKER = ' | Cleared: '

export type HaltDetailView =
  | { kind: 'withheld' }
  | { kind: 'none' }
  /** Either part may be null: a detail that is only a note, or a diagnosis that was never cleared. */
  | { kind: 'recorded'; diagnosis: string | null; note: string | null }

/**
 * A halt's detail split back into the diagnosis and the note appended when it was cleared.
 *
 * The clear APPENDS `" | Cleared: {note}"` rather than storing the note apart, so a cleared halt's detail is both.
 * Split at the FIRST marker: the diagnosis is written by the ETL's capacity check, and only the clear appends one
 * (once — a repeat clear changes nothing), so anything after it is the note, whatever the note itself says. A
 * withheld detail withholds the note too: the server withheld the whole field.
 */
export function haltDetailView(
  detail: string | null | undefined,
  withheld: boolean | null | undefined,
): HaltDetailView {
  if (withheld === true) return { kind: 'withheld' }
  if (detail == null || detail.trim() === '') return { kind: 'none' }

  const at = detail.indexOf(HALT_CLEARED_MARKER)
  if (at < 0) return { kind: 'recorded', diagnosis: detail.trim(), note: null }
  return {
    kind: 'recorded',
    diagnosis: detail.slice(0, at).trim() || null,
    note: detail.slice(at + HALT_CLEARED_MARKER.length).trim() || null,
  }
}

export type ErrorTextView =
  | { kind: 'withheld' }
  | { kind: 'none' }
  | { kind: 'recorded'; text: string }

/** A recorded error (a run's, an entity's, a connection's, an activity row's) as this caller may see it. */
export function errorTextView(text: string | null | undefined, withheld: boolean | null | undefined): ErrorTextView {
  if (withheld === true) return { kind: 'withheld' }
  if (text == null || text.trim() === '') return { kind: 'none' }
  return { kind: 'recorded', text }
}
